/**
 * @file Service for retrieving entity managers and metadatas.
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { EntityManager, MetadataStore, LocalQueryComparisonOptions, NamingConvention } from 'breeze-client';
import { SettingsService } from './settings.service';
import { ErrorService } from './core/error.service';
var EntityManagerService = /** @class */ (function () {
    /**
     * Creates an instance of EntityManagerService.
     * @param {SettingsService} settingsService Used to retrieve the domain settings.
     * @param {ErrorService} errorService Used to report errors.
     * @memberOf EntityManagerService
     */
    function EntityManagerService(settingsService, errorService) {
        this.settingsService = settingsService;
        this.errorService = errorService;
        /**
         * Domain specific option when loading the metadata.
         * @private
         * @type {{ [domain: string]: MetadataStoreOptions }}
         * @memberOf EntityManagerService
         */
        this.metadataStoreOptions = {
            'planning': {
                localQueryComparisonOptions: new LocalQueryComparisonOptions({
                    name: 'caseSensitiveSQL',
                    isCaseSensitive: true,
                    usesSql92CompliantStringComparison: true
                })
            },
            'insights': {
                namingConvention: NamingConvention.camelCase
            }
        };
        /**
         * The entity managers of all domains.
         * @type {{ [domain: string]: EntityManager }}
         * @memberOf EntityManagerService
         */
        this.managers = {};
        /**
         * The metadatas of all domains.
         * @type {{ [domain: string]: MetadataStore }}
         * @memberOf EntityManagerService
         */
        this.metadatas = {};
    }
    /**
     * Loads all the entity managers and metadatas.
     * @memberOf EntityManagerService
     */
    EntityManagerService.prototype.bootInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var promises;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        promises = [];
                        this.settingsService.settings.frontend.domains.forEach(function (domain) {
                            promises.push(_this.initialize(domain, _this.metadataStoreOptions[domain.name]));
                        });
                        return [4 /*yield*/, Promise.all(promises)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Retrieves the metadata and entity manager of a domain.
     * @param {string} name The name of the domain.
     * @returns {Promise<void>} A promise that concludes when the metadata and entity manager have been loaded.
     * @memberOf EntityManagerService
     */
    EntityManagerService.prototype.initialize = function (domain, options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.metadatas[domain.name] = new MetadataStore(options);
                        return [4 /*yield*/, this.metadatas[domain.name].fetchMetadata(domain.uri, function () {
                                try {
                                    _this.managers[domain.name] = new EntityManager({ serviceName: domain.uri, metadataStore: _this.metadatas[domain.name] });
                                }
                                catch (error) {
                                    _this.managers[domain.name] = null;
                                    console.warn("Service '" + domain.name + "' initialization failed: " + error.message);
                                }
                            }, function (error) {
                                _this.errorService.addError(error);
                            })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    return EntityManagerService;
}());
export { EntityManagerService };
